import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import type { Video } from '../types';

interface VideoModalProps {
    video: Video;
    onClose: () => void;
}

const XIcon: React.FC<{ className?: string }> = ({ className }) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;

const VideoModal: React.FC<VideoModalProps> = ({ video, onClose }) => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = 'auto';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const videoSrc = `https://www.youtube.com/embed/${video.youtubeVideoId}?autoplay=1&rel=0&modestbranding=1`;

    const modalContent = (
        <div
            className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={`Video player for ${video.title}`}
        >
            <div
                className="relative bg-black w-full max-w-4xl aspect-video transform transition-transform duration-300 animate-slide-up"
                onClick={e => e.stopPropagation()}
            >
                <iframe
                    src={videoSrc}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                ></iframe>
                 <button
                    onClick={onClose}
                    className="absolute -top-4 -right-4 md:-top-6 md:-right-6 bg-white text-black rounded-full p-2 shadow-lg hover:scale-110 transition-transform z-10"
                    aria-label="Close video player"
                >
                    <XIcon className="w-6 h-6"/>
                </button>
            </div>
            <style>{`
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
                @keyframes slide-up { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
                .animate-slide-up { animation: slide-up 0.3s ease-out forwards; }
            `}</style>
        </div>
    );

    const modalRoot = document.getElementById('modal-root');
    if (!modalRoot) return null;
    return ReactDOM.createPortal(modalContent, modalRoot);
};

export default VideoModal;